<template>
  <div
    class="change-password"
    data-cy="change-password-container"
  >
    <ngc-input-text
      class="password-field"
      label="Current password"
      type="password"
      :value="currentPassword"
      :error-text="currentPasswordError"
      :help-text="currentPasswordHint"
      data-cy="current-password-field"
      @input="onCurrentPasswordInput"
      @change="enableCurrentPassValidation"
    />
    <ngc-input-text
      class="password-field"
      label="New password"
      type="password"
      :value="newPassword"
      :error-text="newPasswordError"
      :help-text="newPasswordHint"
      data-cy="new-password-field"
      @input="onNewPasswordInput"
      @change="enableNewPassValidation"
    />
    <ngc-input-text
      class="password-field"
      label="Confirm new password"
      type="password"
      :value="confirmPassword"
      :error-text="confirmPasswordHint"
      data-cy="confirm-password-field"
      @input="onConfirmPasswordInput"
      @change="enableConfirmPassValidation"
    />
  </div>
</template>

<script>
import snValidationsService from '~/services/snValidationsService.js'
import { updateUserPassword } from '../graphql/signing_room_queries.gql'

import { mapMutations } from 'vuex'

export default {
  name: 'ChangePassword',
  props: {
    initiatorUserGuid: {
      type: String,
      default: '',
      required: true
    }
  },
  data () {
    return {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      currentPasswordHint: '',
      currentPasswordError: '',
      newPasswordHint: 'Password must have at least 9 characters, one uppercase letter, one lowercase letter, and one number',
      newPasswordError: '',
      confirmPasswordHint: '',
      currentPassValidationEnabled: false,
      newPassValidationEnabled: false,
      confirmPassValidationEnabled: false,
      currentPassIsValid: false,
      newPassIsValid: false,
      confirmPassIsValid: false,
      showSuccessToast: false,
      showErrorToast: false,
      successToastMessage: '',
      successToastDescription: '',
      errorToastMessage: ''
    }
  },

  computed: {
    saveEnabled () {
      return this.currentPassIsValid && this.newPassIsValid && this.confirmPassIsValid
    }
  },

  // Save lives in the dialog footer, so the parent owns the button and needs
  // this component's enabled state pushed up to it.
  watch: {
    saveEnabled: {
      immediate: true,
      handler (val) {
        this.$emit('save-disabled', !val)
      }
    }
  },

  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType']),
    onCurrentPasswordInput (event) {
      this.currentPassword = event.detail.value
      this.validateCurrentPassword()
    },
    onNewPasswordInput (event) {
      this.newPassword = event.detail.value
      this.validateNewPassword()
    },
    onConfirmPasswordInput (event) {
      this.confirmPassword = event.detail.value
      this.validateConfirmPassword()
    },
    validateCurrentPassword () {
      if (this.currentPassword) {
        this.$emit('change', true)
      } else {
        this.$emit('change', false)
      }
      if (this.currentPassValidationEnabled) {
        this.currentPassIsValid = !!this.currentPassword
        this.currentPasswordError = this.currentPassIsValid ? '' : this.currentPasswordError
        this.currentPasswordHint = ''
      }
    },
    validateNewPassword () {
      if (this.newPassword) {
        this.$emit('change', true)
      } else {
        this.$emit('change', false)
      }
      if (this.newPassValidationEnabled) {
        this.newPassIsValid = snValidationsService.password(this.newPassword)
        const newPassMessage = 'Password must have at least 9 characters, one uppercase letter, one lowercase letter, and one number'
        this.newPasswordError = this.newPassIsValid ? '' : newPassMessage
        this.newPasswordHint = this.newPassIsValid ? newPassMessage : ''
      }
      if (this.confirmPassValidationEnabled) {
        this.validateConfirmPassword()
      }
    },
    validateConfirmPassword () {
      if (this.confirmPassword) {
        this.$emit('change', true)
      } else {
        this.$emit('change', false)
      }
      if (!this.confirmPassValidationEnabled && this.confirmPassword.length > 8) {
        this.enableConfirmPassValidation()
      } else if (this.confirmPassValidationEnabled) {
        this.confirmPassIsValid = this.newPassword === this.confirmPassword
        this.confirmPasswordHint = this.confirmPassIsValid ? '' : 'Passwords do not match'
      }
    },
    enableCurrentPassValidation () {
      this.currentPassValidationEnabled = true
      this.validateCurrentPassword()
    },
    enableNewPassValidation () {
      this.newPassValidationEnabled = true
      this.validateNewPassword()
    },
    enableConfirmPassValidation () {
      this.confirmPassValidationEnabled = true
      this.validateConfirmPassword()
    },
    handleSave () {
      this.$apollo
        .mutate({
          mutation: updateUserPassword,
          variables: {
            currentPassword: this.currentPassword,
            newPassword: this.newPassword,
            confirmPassword: this.confirmPassword,
            initiatorUserGuid: this.initiatorUserGuid
          }
        })
        .then(({ data }) => {
          const { message } = data.updateUserPassword
          if (message === 'Password Updated') {
            Object.assign(this.$data, this.$options.data())
            this.successToastMessage = 'Password updated'
            this.successToastDescription = 'You must login again. You will be redirected momentarily.'
            this.setSuccessToast({ message: this.successToastMessage, description: this.successToastDescription })
            setTimeout(() => { window.location.href = '/login' }, 4000)
          } else if (message === 'Incorrect current password') {
            this.currentPassword = ''
            this.currentPasswordError = message
            this.currentPassIsValid = false
            this.errorToastMessage = message
            this.setErrorToastType({ description: this.errorToastMessage })
          } else if (message === 'Invalid User') {
            window.location.href = '/login'
          } else {
            this.errorToastMessage = message
            this.showErrorToast = true
          }
        }).catch(e => {
          this.errorToastMessage = 'An error occurred while updating this information. Please try again later.'
          this.setErrorToastType({ description: this.errorToastMessage })
        })
    }
  }
}
</script>

<style scoped>

.change-password {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
}

.password-field {
  width: 528px;
  max-width: 100%;
}

</style>
